import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CustomerAccountType, Gateway, roundDecimals, TimeBucketUnits, } from '@iotv/datamodel-core';
import { SortOrder } from '@iotv/iotv-v3-types';
import { Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { getGraphQueryRequestV2 } from '../../../data/daoFunctions/GraphQueries/recursiveFilteredGraphQuery';
import { NetWorkDAO } from '../../../data/NetworkDAO';
import { getGatewayDevices } from '../../DeviceManagement/tabs/Wideawake/GatewayDevices';
import { GatewayListViewDefintion } from '../commonGatewayViewDefinition';
import GatewayAnalysis from './GatewayAnalysis/GatewayAnalysis';
import { getCustomersGatewaysQuery, getGatewayProperty } from './query';
const maxCols = 20;
const todayQuery = async () => {
    const query = {
        units: TimeBucketUnits.HOUR.valueOf(),
        duration: 1,
        fromDateValue: dayjs().subtract(1, 'day'),
        toDateValue: dayjs(),
        sortOrder: SortOrder.DESCENDING,
    };
    const { data } = await NetWorkDAO.geGatewayMetricsFromTimestream(query);
    if (data) {
        return data;
    }
};
const yesterdayQuery = async () => {
    const query = {
        units: TimeBucketUnits.DAY.valueOf(),
        duration: 1,
        fromDateValue: dayjs().subtract(2, 'day'),
        toDateValue: dayjs().subtract(1, 'day'),
        sortOrder: SortOrder.DESCENDING,
    };
    const { data } = await NetWorkDAO.geGatewayMetricsFromTimestream(query);
    if (data) {
        return data;
    }
};
export const formatGateway = async (parsedGateways, mapper) => {
    const todayMetric = await todayQuery();
    const yesterdayMetric = await yesterdayQuery();
    const mappedGateways = parsedGateways.map(async (gateway) => {
        const devCount = await getGatewayDevices({ sourceNode: gateway, ExclusiveStartKey: undefined, Limit: undefined });
        let property = '';
        let customer = '';
        const groupOrProperty = mapper.getParentNodes(gateway).filter((val) => val.name !== '"All Customer Gateways"'); //get properties or customers
        if (groupOrProperty.length > 0) {
            let hasProperty = groupOrProperty.find((val) => val.type === 'Property');
            const propertyOrCustomer = groupOrProperty.map((val) => mapper.getParentNodes(val)).flat();
            const foundProperty = propertyOrCustomer.find((val) => val.type === 'Property');
            if (foundProperty) {
                hasProperty = foundProperty;
            }
            if (hasProperty && hasProperty.name) {
                property = hasProperty.name;
            }
            const hasCustomer = propertyOrCustomer.find((val) => val.type === 'Customer' && val.accountType !== CustomerAccountType.V);
            if (hasCustomer && hasCustomer.name) {
                customer = hasCustomer.name;
            }
        }
        let dailyUpCount = 0;
        let dailyDownCount = 0;
        let fCntJump = 0;
        let lastHourDevCount = 0;
        let lastHourMsgCount = 0;
        let lastDayMsgCount = 0;
        let lastDayDeviceCount = 0;
        const todayRelevantBuckets = todayMetric?.filter((val) => val.id === gateway.pk).sort((a, b) => b.bucketEnd - a.bucketEnd) || [];
        const yesterdayRelevantBuckets = yesterdayMetric?.filter((val) => val.id === gateway.pk).sort((a, b) => b.bucketEnd - a.bucketEnd) || [];
        if (todayRelevantBuckets.length > 0 && yesterdayRelevantBuckets.length > 0) {
            const latestBucket = todayRelevantBuckets[0];
            const yesterdayBucket = yesterdayRelevantBuckets[yesterdayRelevantBuckets.length - 1];
            const aggregation = todayRelevantBuckets.reduce((acc, cur) => {
                acc.droppedMsgs += cur.droppedMsgs;
                acc.uplinks += cur.uplinks;
                acc.downlinks += cur.downlinks;
                acc.messagesPerPeriodMax += cur.messagesPerPeriodMax;
                acc.currentRate += cur.currentRate;
                acc.devices += cur.devices;
                acc.rssiMean += cur.rssiMean;
                acc.rssiStdDev += cur.rssiStdDev;
                acc.snrMean += cur.snrMean;
                acc.snrStdDev += cur.snrStdDev;
                acc.sfMean += cur.sfMean;
                acc.bucketStart += cur.bucketStart;
                acc.bucketEnd += cur.bucketEnd;
                return acc;
            }, {
                droppedMsgs: 0,
                uplinks: 0,
                downlinks: 0,
                messagesPerPeriodMax: 0,
                currentRate: 0,
                devices: 0,
                rssiMean: 0,
                rssiStdDev: 0,
                snrMean: 0,
                snrStdDev: 0,
                sfMean: 0,
                bucketStart: 0,
                bucketEnd: 0,
            });
            dailyUpCount = aggregation.uplinks;
            dailyDownCount = aggregation.uplinks;
            fCntJump = aggregation.droppedMsgs;
            lastHourDevCount = latestBucket.devices;
            lastHourMsgCount = latestBucket.messagesPerPeriodMax;
            lastDayMsgCount = yesterdayBucket.uplinks;
            lastDayDeviceCount = roundDecimals(aggregation.devices / todayRelevantBuckets.length, 1);
        }
        let acceptance = '';
        if (dailyUpCount !== 0) {
            acceptance = roundDecimals((dailyUpCount / (fCntJump + dailyUpCount)) * 100, 3);
        }
        return {
            ...gateway,
            position: `${gateway?.location?.lat || '??'}, ${gateway?.location?.lng || '??'}`,
            property: property,
            customer: customer,
            deviceCount: devCount.length,
            channelMessage: '',
            dailyUpCount,
            dailyDownCount,
            fCntJump,
            lastHourDevCount,
            lastHourMsgCount,
            lastDayMsgCount,
            lastDayDeviceCount,
            acceptance,
            battery: 'N/A',
        };
    });
    return mappedGateways;
};
const GatewayList = ({ userCustomers }) => {
    const externalColumnVisibility = Object.fromEntries(Object.keys(GatewayListViewDefintion)
        .slice(maxCols)
        .map((k) => [k, false]));
    const [gateways, setGateways] = useState(undefined);
    const [gatewayPropertyMapper, setGatewayPropertyMapper] = useState();
    const [formattedGateways, setFormattedGateways] = useState(undefined);
    useEffect(() => {
        const getGateways = async () => {
            const allCustomers = userCustomers?.getNodesOfType('Customer') || [];
            if (allCustomers.length !== 0) {
                const gatewaysPromise = await getGraphQueryRequestV2({
                    contextObjects: allCustomers,
                    query: getCustomersGatewaysQuery().query,
                });
                if (gatewaysPromise.err === null && gatewaysPromise.data !== undefined) {
                    const gateways = (gatewaysPromise.data?.getNodesOfType('Gateway') || []).filter((val) => val.eui !== undefined);
                    setGateways(gateways.map((g) => new Gateway(g)));
                    getGraphQueryRequestV2({ contextObjects: gateways, query: getGatewayProperty().query }).then((res) => {
                        if (res.data)
                            setGatewayPropertyMapper(res.data);
                    });
                }
            }
        };
        getGateways();
    }, [userCustomers]);
    useEffect(() => {
        const setGatewayDetails = async (desiredGateways, mapper) => {
            const mappedGateways = await formatGateway(desiredGateways, mapper);
            Promise.all(mappedGateways).then((res) => {
                setFormattedGateways(res);
            });
        };
        if (gateways && gatewayPropertyMapper) {
            setGatewayDetails(gateways, gatewayPropertyMapper);
        }
    }, [gateways, gatewayPropertyMapper]);
    return (_jsx(Stack, { gap: 1, children: _jsxs(Stack, { children: [_jsx(Stack, { direction: "row", children: _jsx(Typography, { component: "h6", variant: "h6", children: "Gateway List" }) }), _jsx(GatewayAnalysis, { gatewayList: gateways ?? [] })] }) }));
};
export default GatewayList;
